import React, { useState, useEffect } from "react";
import { useFormikContext } from "formik";
import { Row, Col, Form } from "react-bootstrap";
import { Autocomplete, Box } from "@mui/material";
import BootstrapTextField from "Common/Components/InputComponent/BootstrapTextField";
import { ServiceQuotationFormValues } from "../serviceInitialValues";
import { MecordColors } from "Constants/Color";
import { autocompleteDropdownStyles } from "Constants/Style";
import { Axios } from "api/axios";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { toast } from "react-toastify";

interface User {
  user_id: string;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
}

const AssignEngineersSection: React.FC = () => {
  const { values, setFieldValue } = useFormikContext<ServiceQuotationFormValues>();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const selectedTechnicianIds = (values.assigned_technician || []).map(
    (t: any) => t.user_id || t.id
  );

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await Axios.get(
          API_ENDPOINT.USER_MANAGEMENT.USER.GET_USER_DROPDOWN_BY_TYPE("Technician")
        );
        if (response.data.success || response.data) {
          setUsers(response.data.data || response.data);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        toast.error("Failed to fetch users");
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, []);

  const handleEngineerAdd = (user: User) => {
    if (!selectedTechnicianIds.includes(user.user_id)) {
      const newEngineer = {
        id: user.user_id,
        user_id: user.user_id,
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        username: user.username || "",
      };
      setFieldValue("assigned_technician", [
        ...(values.assigned_technician || []),
        newEngineer,
      ]);
    }
  };

  const handleEngineerRemove = (engineerId: string) => {
    setFieldValue(
      "assigned_technician",
      (values.assigned_technician || []).filter(
        (t: any) => (t.user_id || t.id) !== engineerId
      )
    );
  };

  const availableEngineers = users.filter(
    (user) => !selectedTechnicianIds.includes(user.user_id)
  );

  const disabled = Boolean((values as any).has_order || (values as any).sales_order_service_id);

  return (
    <div className="rounded mb-3">
      <div className="d-flex justify-content-between align-items-center mb-3 gap-1">
        <h5 className="fw-semibold">Add Engineers</h5>
      </div>

      <Row
        className="g-3 pb-3 px-1 rounded mb-2"
        style={{ backgroundColor: MecordColors?.LightThemeBGColor }}
      >
        <Col xs={12} md={12}>
          <Form.Group>
            <Form.Label>Select Engineers</Form.Label>
            <Autocomplete
              options={availableEngineers}
              loading={loading}
              disabled={disabled}
              getOptionLabel={(option) =>
                `${option.first_name} ${option.last_name}`.trim() || option.username || ""
              }
              isOptionEqualToValue={(option, value) =>
                option.user_id === (value.user_id || (value as any).id)
              }
              onChange={(_: any, newValue: User | null) => {
                if (newValue) {
                  handleEngineerAdd(newValue);
                }
              }}
              ListboxProps={autocompleteDropdownStyles.ListboxProps}
              renderOption={(props, option, { selected }) => (
                <li {...props} style={{ padding: 0 }}>
                  <Box
                    sx={{
                      px: 1.5,
                      py: 0.8,
                      width: "100%",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      cursor: "pointer",
                      "&:hover": {
                        backgroundColor: MecordColors.Hover,
                        color: MecordColors.ActiveText,
                      },
                      ...(selected && {
                        backgroundColor: MecordColors.ActiveBackground,
                        color: MecordColors.ActiveText,
                      }),
                    }}
                  >
                    <span>{`${option.first_name} ${option.last_name}`.trim()}</span>
                  </Box>
                </li>
              )}
              renderInput={(params) => (
                <BootstrapTextField
                  {...params}
                  placeholder="Search Engineer"
                  size="small"
                  sx={autocompleteDropdownStyles.inputSx}
                />
              )}
            />
          </Form.Group>
        </Col>
      </Row>

      <Row className="g-3 rounded">
        {(values.assigned_technician || []).length > 0 && (
          <Col xs={12} md={12}>
            <div className="d-flex flex-wrap gap-2">
              {(values.assigned_technician || []).map((tech: any) => {
                const techId = tech.user_id || tech.id;
                const name =
                  tech.first_name || tech.last_name
                    ? `${tech.first_name || ""} ${tech.last_name || ""}`.trim()
                    : tech.username || "";
                const initials = (
                  (tech.first_name?.charAt(0) || "") +
                  (tech.last_name?.charAt(0) || "")
                ).toUpperCase();

                return (
                  <div
                    key={techId}
                    className="d-flex align-items-center px-2 py-1 rounded gap-2"
                    style={{
                      background: MecordColors?.LightThemeBGColor,
                      border: `1px solid ${MecordColors?.ThemeBGColor}`,
                      fontSize: "14px",
                    }}
                  >
                    <span
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{
                        width: "24px",
                        height: "24px",
                        fontSize: "0.75rem",
                        fontWeight: "bold",
                        backgroundColor: "#fff",
                        color: MecordColors.ThemeBGColor,
                      }}
                    >
                      {initials}
                    </span>
                    {name}
                    {!disabled && (
                      <span
                        style={{
                          marginLeft: "4px",
                          cursor: "pointer",
                          color: "red",
                        }}
                        onClick={() => handleEngineerRemove(techId)}
                      >
                        ✕
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Col>
        )}
      </Row>
    </div>
  );
};

export default AssignEngineersSection;
